<template>
  <v-breadcrumbs v-if="!isMobile || (isMobile && crumbs.length > 1)" :items="crumbs">
    <template #item="{ item, index }">
      <div class="item-flex">
        <span
          :class="{ 'crumb-link': (!!item.to || !!item.action) && index !== crumbs.length - 1 }"
          @click.stop.prevent="index !== crumbs.length - 1 && handleCrumbClick(item)"
        >{{ item.title }}</span>
        <span v-if="item.siblings && item.siblings.length > 1 && item.siblings.every(e => 'icon' in e)" class="breadcrumb-path-siblings">
          <BaseMenu
            :placement="'bottom'"
            :offset="[0, 16]"
          >
            <!-- allow click = false to remove active state on click  -->
            <IconButton
              :id="`${item.title.trim()}`"
              :mirrored="show"
              class="breadcrumb-dropdown-icon"
              :icon="'expand_more'"
              :width="22"
              :height="22"
              :allowClick="false"
              @click.stop.prevent="toggleShow"
            />
            <template #menu="{closeMenu}">
              <ul>
                <li
                  v-for="(s, idx) in item.siblings.filter(i => i.meta && Object.keys(i.meta).length > 0)"
                  :key="`${s.meta.breadCrumb.trim()}_${idx}`"
                >
                  <BaseMenuButton
                    :disabled="s.meta.breadCrumb === item.title"
                    @click.stop="navigate(s.name), closeMenu(), toggleShow()"
                  >
                    <SVGIcon
                      :iconName="s.meta.icon"
                      :width="'22px'"
                      :height="'22px'"
                    />
                    <span>{{ s.meta.breadCrumb }}</span>
                  </BaseMenuButton>
                </li>
              </ul>
            </template>
          </BaseMenu>
        </span>
      </div>
    </template>
  </v-breadcrumbs>
</template>
<script>
import IconButton from '@/components/IconButton.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import { useMobile } from '@/composables/useMobile';
import { goto_projects, goto_experiments, goto_validations, goto_datasets } from '@/assets/js/links.js';

export default {
  name: 'BreadcrumbNavigation',
  components: {
    IconButton,
    BaseMenu,
    BaseMenuButton,
    SVGIcon,
  },
  setup() {
    const { isMobile } = useMobile();
    return {
      isMobile,
    };
  },
  data() {
    return {
      show: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    crumbs() {
      const result = [];
      this.$route.matched
        .filter((r) => r.meta?.breadCrumb) // only include routes that define a breadcrumb
        .forEach((routeRecord, idx, matched) => {
          // Synthesize ancestor crumbs from meta.breadCrumbParents
          const parents = routeRecord.meta.breadCrumbParents || [];
          parents.forEach((p) => {
            const title = p.titleParam ? this.$route.params[p.titleParam] : p.title;
            if (!title) return;
            result.push({
              to: p.toName
                ? { name: p.toName, params: this.$route.params }
                : null,
              action: p.action || null,
              title,
              siblings: null,
            });
          });

          const parent = matched[idx - 1];
          const targetName = routeRecord.meta.breadCrumbTo || routeRecord.name;
          result.push({
            path: routeRecord.path, // still has :params if defined
            to: {
              name: targetName, // safer than building path manually
              params: this.$route.params, // keep current params (e.g. project_id)
            },
            action: routeRecord.meta.breadCrumbAction || null,
            title: routeRecord.meta.breadCrumb,
            siblings: parent?.children
              ? parent.children.filter(
                (item) => (!item.permissions
                  || item.permissions.some((role) => this.user.role === role))
                && item.meta?.breadCrumb,
              )
              : null,
          });
        });
      return result;
    },
  },
  methods: {
    navigate(name) {
      this.$router.push({ name });
    },
    handleCrumbClick(item) {
      if (item.action === 'projects') {
        goto_projects();
        return;
      }
      if (item.action === 'experiments-from-store') {
        const projectId = this.$store.state.projects?.currentProject?.id;
        if (projectId) {
          goto_experiments(projectId);
          return;
        }
      }
      if (item.action === 'validations-from-store') {
        const projectId = this.$store.state.projects?.currentProject?.id;
        const experimentId = this.$store.state.experiments?.currentExperiment?.id;
        if (projectId && experimentId) {
          goto_validations(projectId, experimentId);
          return;
        }
      }
      if (item.action === 'datasets-from-store') {
        const projectId = this.$store.state.projects?.currentProject?.id;
        if (projectId) {
          goto_datasets(projectId);
          return;
        }
      }
      if (item.to) {
        this.$router.push(item.to);
      }
    },
    toggleShow() {
      this.show = !this.show;
    },
  },
};
</script>
<style lang="scss">
.item-flex {
  display: flex;
  padding: 5px;
  flex-wrap: nowrap;
  overflow-x: hidden;
  overflow-y: hidden;
  span {
    width: fit-content;
    white-space: nowrap;
    font-size: 1.2rem;
    font-weight: 400;
  }
}

.v-breadcrumbs {
  padding: 0;
}

.v-breadcrumbs .v-breadcrumbs-item--link,
.v-breadcrumbs .v-icon {
  font-size: 1.2rem;
}

.v-breadcrumbs .v-breadcrumbs-divider {
  font-size: 1.2rem;
  font-weight: 300;
  padding: 0;
}

.v-breadcrumbs li.v-breadcrumbs-item--disabled:first-child{
  opacity: 1;
}

.v-breadcrumbs li.v-breadcrumbs-item--disabled:not(:first-child) {
  opacity: 1;
  text-decoration: underline;
  text-decoration-thickness: 2px;
}

.breadcrumb-path-siblings {
  margin-left: 8px;
  pointer-events: auto;
}

.breadcrumb-dropdown-icon {
  z-index: 4;
}

.breadcrumb-dropdown-icon.active {
  color: var(--color-white) !important;
}

.item-flex span.crumb-link {
  cursor: pointer;
  padding: 4px 10px;
  display: inline-block;
  transition: color 0.075s ease, text-decoration-color 0.075s ease;
}

.item-flex span:hover,
.item-flex span.crumb-link:hover,
.item-flex:hover span.crumb-link {
  color: #ffffff !important;
}

.item-flex span.crumb-link:hover,
.item-flex:hover span.crumb-link {
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
}

.item-flex span.crumb-link:active {
  transform: translateY(1px);
}
</style>
